import type { User, Organization, UserRole, PathwayProgress, OjtSession, OjtElement, PathwayDoc, PathwayVersion, Pathway, Activity as ActivityType, ActivityEntityType, ActivityAction, CourseTemplate, TrainingRoomCourse, BlockTrainingRoomLink, PracticalEvaluation, AssessmentAttempt, BlockUpload, ApiVerification, PathwayAuthorisation, OjtMessage } from '../types';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  connectAuthEmulator,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  limit,
  writeBatch,
  connectFirestoreEmulator,
  onSnapshot
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, connectStorageEmulator } from 'firebase/storage';

// --- CONFIGURATION ---

// Root collection for this app - isolates data from other apps in the same Firebase project
const APP_ROOT = 'soloready';
const STORAGE_ROOT = 'soloready';

// Helper to get collection reference under the app root
const appCollection = (collectionName: string) => collection(db, APP_ROOT, 'data', collectionName);
const appDoc = (collectionName: string, docId: string) => doc(db, APP_ROOT, 'data', collectionName, docId);

// Helper to get a subcollection reference under a pathway document
const pathwayVersionsCollection = (pathwayId: string) => collection(db, APP_ROOT, 'data', 'pathways', pathwayId, 'versions');
const pathwayVersionDoc = (pathwayId: string, versionId: string) => doc(db, APP_ROOT, 'data', 'pathways', pathwayId, 'versions', versionId);

const firebaseConfig = {
  apiKey: "AIzaSyBX_c2sYhMirjXD9NtpnWigj6SqGKN7rYE",
  authDomain: "tr-staging-8964c.firebaseapp.com",
  databaseURL: "https://tr-staging-8964c.firebaseio.com",
  projectId: "tr-staging-8964c",
  storageBucket: "tr-staging-8964c.appspot.com",
  messagingSenderId: "570768664029",
  appId: "1:570768664029:web:5575553a09069465c60234",
  measurementId: "G-BS5LNF79SR"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Configure auth persistence to keep users logged in
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Failed to set auth persistence:', error);
});

// Connect to emulators in development mode
// Check for Vite's import.meta.env.DEV or Node's NODE_ENV
// Temporarily disabled to connect to live backend
/*
const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV || process.env.NODE_ENV === 'development';
if (isDev) {
  console.log('🔧 Connecting to Firebase Emulators...');
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  console.log('✅ Connected to Firebase Emulators');
}
*/

// Storage helper - prefixes all paths with app root
export const getStoragePath = (path: string) => `${STORAGE_ROOT}/${path}`;
export const getStorageRef = (path: string) => ref(storage, getStoragePath(path));

// --- AUTH SERVICE ---
export const AuthService = {
  async login(email: string, password?: string): Promise<User> {
    if (!password) throw new Error("Password is required for login");
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const fbUser = userCredential.user;

    // Read from root userdetails collection (shared across all apps)
    const userDocRef = doc(db, 'userdetails', fbUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      
      // Determine roles - admin if teamsadminnest exists with content
      const hasAdminAccess = data.teamsadminnest && Object.keys(data.teamsadminnest).length > 0;
      const roles: UserRole[] = hasAdminAccess ? ['ADMIN'] : ['CANDIDATE'];
      
      // Build user object with correct field mappings
      const userData: User = {
        id: fbUser.uid,
        email: data.email || fbUser.email || email,
        name: data.name || fbUser.displayName || 'User',
        phoneNumber: data.phoneNumber || fbUser.phoneNumber || undefined,
        roles: roles,
        orgIds: data.teamsadminlist || [], // Get org IDs from teamsadminlist
        teamsadminnest: data.teamsadminnest || {}, // Get nested hierarchy
        activeOrgId: data.SR_activeteam || data.activeOrgId, // Load from SR_activeteam
        activeOrgIncludesSubTeams: data.SR_includesubteams ?? data.SR_activeOrgIncludesSubTeams ?? data.activeOrgIncludesSubTeams,
        lastLogin: Date.now()
      };
      
      // Update last login timestamp
      await updateDoc(userDocRef, { lastLogin: Date.now() });

      return userData;
    } else {
      throw new Error("User profile not found in database.");
    }
  },

  async register(email: string, name: string, role: UserRole, password?: string): Promise<User> {
    if (!password) throw new Error("Password is required for registration");

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const fbUser = userCredential.user;

    // Create userdetails document matching trainingroom structure
    const userDetailsDoc = {
      active: true,
      name: name,
      email: email,
      courses: {},
      teamslist: [],
      status: "Active",
      dashboard: {
        last10teamedits: [],
        last5usersadded: [],
        usersonhold: [],
        last5courseedits: []
      },
      courselist: [],
      teamsnest: {},
      teamsadminlist: [], // Will be populated when user joins/creates orgs
      teamsadminnest: {},
      lastLogin: Date.now()
    };

    // Write to root userdetails collection (shared across all apps)
    await setDoc(doc(db, 'userdetails', fbUser.uid), userDetailsDoc);

    // Return User object for SoloReady
    const newUser: User = {
      id: fbUser.uid,
      email,
      name,
      roles: [role],
      orgIds: [],
      lastLogin: Date.now()
    };

    return newUser;
  },

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  },

  async loginWithPhone(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) {
    return await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
  },

  createRecaptchaVerifier(containerId: string): RecaptchaVerifier {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id '${containerId}' not found`);
    }
    return new RecaptchaVerifier(auth, containerId, {
      size: 'normal',
      callback: () => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
      }
    });
  },

  async logout(): Promise<void> {
    await signOut(auth);
  },

  subscribeToAuth(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        callback(null);
        return;
      }

      try {
        // Read from root userdetails collection (shared across all apps)
        const userDocRef = doc(db, 'userdetails', fbUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          
          // Determine roles - admin if teamsadminnest exists with content
          const hasAdminAccess = data.teamsadminnest && Object.keys(data.teamsadminnest).length > 0;
          const roles: UserRole[] = hasAdminAccess ? ['ADMIN'] : ['CANDIDATE'];
          
          // Build user object with correct field mappings
          const userData: User = {
            id: fbUser.uid,
            email: data.email || fbUser.email || '',
            name: data.name || fbUser.displayName || 'User',
            phoneNumber: data.phoneNumber || fbUser.phoneNumber || undefined,
            roles: roles,
            orgIds: data.teamsadminlist || [], // Get org IDs from teamsadminlist
            teamsadminnest: data.teamsadminnest || {}, // Get nested hierarchy
            activeOrgId: data.SR_activeteam || data.activeOrgId, // Load from SR_activeteam
            activeOrgIncludesSubTeams: data.SR_includesubteams ?? data.SR_activeOrgIncludesSubTeams ?? data.activeOrgIncludesSubTeams,
            lastLogin: data.lastLogin
          };
          
          callback(userData);
        } else {
          console.warn("User authenticated but no profile found.");
          callback(null);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        callback(null);
      }
    });
  },

  async updateUserProfile(userId: string, updates: Partial<User> | Record<string, any>): Promise<User> {
    // Update root userdetails collection
    console.log('💾 Updating user profile:', { userId, updates });
    const userRef = doc(db, 'userdetails', userId);
    await updateDoc(userRef, updates);
    console.log('✅ User profile updated in Firestore');
    const updatedDoc = await getDoc(userRef);
    return { ...(updatedDoc.data() as User), id: userId };
  },

  async getUser(userId: string): Promise<User | null> {
    // Read from root userdetails collection
    const userRef = doc(db, 'userdetails', userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const data = userDoc.data();
      console.log('📖 getUser raw data:', { userId, SR_activeteam: data.SR_activeteam, activeOrgId: data.activeOrgId });
      
      // Determine roles - admin if teamsadminnest exists with content
      const hasAdminAccess = data.teamsadminnest && Object.keys(data.teamsadminnest).length > 0;
      const roles: UserRole[] = hasAdminAccess ? ['ADMIN'] : ['CANDIDATE'];
      
      // Map Firestore fields to User object
      const userData: User = {
        id: userId,
        email: data.email || '',
        name: data.name || 'User',
        phoneNumber: data.phoneNumber || undefined,
        roles: roles,
        orgIds: data.teamsadminlist || [],
        teamsadminnest: data.teamsadminnest || {},
        activeOrgId: data.SR_activeteam || data.activeOrgId, // Load from SR_activeteam
        activeOrgIncludesSubTeams: data.SR_includesubteams ?? data.SR_activeOrgIncludesSubTeams ?? data.activeOrgIncludesSubTeams,
        lastLogin: data.lastLogin
      };
      
      console.log('📖 getUser mapped data:', { activeOrgId: userData.activeOrgId, activeOrgIncludesSubTeams: userData.activeOrgIncludesSubTeams });
      
      return userData;
    }
    return null;
  },

  async getCurrentUser(): Promise<User | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return this.getUser(currentUser.uid);
  }
};

// --- DATA SERVICE ---
export const DataService = {
  async getOrgs(userId: string): Promise<Organization[]> {
    // Read from root userdetails collection
    const userRef = doc(db, 'userdetails', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return [];
    
    const userData = userSnap.data() as User;
    const orgIds = userData.orgIds || [];

    if (orgIds.length === 0) return [];

    // Note: Firestore document ID query uses '__name__' field
    const q = query(appCollection('organizations'), where('__name__', 'in', orgIds));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as Organization));
  },

  async createOrg(userId: string, name: string, description: string, parentId: string | null = null): Promise<Organization> {
    const newOrgData = {
      name,
      description,
      parentId,
      createdAt: Date.now(),
      createdBy: userId 
    };

    const docRef = await addDoc(appCollection('organizations'), newOrgData);
    const newOrg = { id: docRef.id, ...newOrgData };

    // Update root userdetails collection
    const userRef = doc(db, 'userdetails', userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const userData = userSnap.data() as User;
      const currentOrgs = userData.orgIds || [];
      
      const updates: Partial<User> = {
        orgIds: [...currentOrgs, newOrg.id]
      };
      
      if (!userData.activeOrgId) {
        updates.activeOrgId = newOrg.id;
      }

      await updateDoc(userRef, updates);
    }

    return newOrg;
  },

  async updateOrg(orgId: string, updates: Partial<Organization>): Promise<void> {
    const orgRef = appDoc('organizations', orgId);
    await updateDoc(orgRef, updates);
  },
  
  async deleteOrg(orgId: string): Promise<void> {
    await deleteDoc(appDoc('organizations', orgId));
  },

  async getUsersByOrg(orgId: string): Promise<User[]> {
    const q = query(appCollection('userdetails'), where('orgIds', 'array-contains', orgId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  },

  async getCandidates(orgId: string): Promise<User[]> {
      const q = query(
          appCollection('userdetails'), 
          where('orgIds', 'array-contains', orgId),
          where('role', '==', 'CANDIDATE')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  },

  async getLeadHands(orgId: string): Promise<User[]> {
      const q = query(
          appCollection('userdetails'), 
          where('orgIds', 'array-contains', orgId),
          where('role', '==', 'LEAD_HAND')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  },

  async getAdmins(orgIds: string[]): Promise<User[]> {
      // Firestore limitation: array-contains-any limited to 10.
      // We pass the orgId hierarchy here to get admins from all levels
      if (orgIds.length === 0) return [];
      const safeOrgIds = orgIds.slice(0, 10); // Safeguard
      
      const q = query(
          appCollection('userdetails'), 
          where('orgIds', 'array-contains-any', safeOrgIds),
          where('role', '==', 'ADMIN')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  },

  async createUser(orgId: string, email: string, name: string, role: UserRole): Promise<void> {
    await addDoc(appCollection('userdetails'), {
        email,
        name,
        role,
        orgIds: [orgId],
        createdAt: Date.now()
    });
  },

  async updateUser(userId: string, data: Partial<User>): Promise<void> {
    await updateDoc(appDoc('userdetails', userId), data);
  },

  async deleteUser(userId: string): Promise<void> {
    await deleteDoc(appDoc('userdetails', userId));
  },

  async inviteUser(orgId: string, email: string, name: string, role: UserRole): Promise<void> {
    // Deprecated alias for createUser
    return this.createUser(orgId, email, name, role);
  },

  // --- PATHWAY METHODS (PathwayDoc + versions subcollection) ---

  /** Get all pathway parent documents for an org (excludes legacy flat-format docs) */
  async getPathways(orgId: string): Promise<Pathway[]> {
    const q = query(appCollection('pathways'), where('orgId', '==', orgId));
    const snap = await getDocs(q);
    // Only return new-format docs (have dateCreated). Old flat-format version docs are ignored.
    const parentDocs = snap.docs
      .filter(d => d.data().dateCreated)
      .map(d => ({ id: d.id, ...d.data() } as PathwayDoc));

    // For each parent doc, load its versions and merge into flat Pathway objects
    const allPathways: Pathway[] = [];
    for (const parent of parentDocs) {
      const versionsSnap = await getDocs(pathwayVersionsCollection(parent.id));
      if (versionsSnap.empty) {
        // Parent doc with no versions yet - create a default entry so it still shows
        allPathways.push({
          id: parent.id,
          rootId: parent.id,
          title: parent.title || 'Untitled',
          role: parent.role || 'Unassigned',
          lastEdited: parent.lastEdited || parent.dateCreated || '',
          status: parent.status || 'DRAFT',
          version: 1.0,
          blocks: [],
          description: parent.description || '',
          soloreadyOutline: parent.soloreadyOutline || '',
        });
      } else {
        for (const vDoc of versionsSnap.docs) {
          const v = { id: vDoc.id, ...vDoc.data() } as PathwayVersion;
          allPathways.push({
            id: v.id,
            rootId: parent.id,
            title: parent.title || 'Untitled',
            role: parent.role || 'Unassigned',
            lastEdited: parent.lastEdited || parent.dateLastUpdated || parent.dateCreated || '',
            status: v.status || parent.status || 'DRAFT',
            version: v.version ?? 1.0,
            blocks: v.blocks || [],
            description: parent.description || '',
            soloreadyOutline: parent.soloreadyOutline || '',
          });
        }
      }
    }
    return allPathways;
  },

  /** Get all versions for a given pathway */
  async getPathwayVersions(pathwayId: string): Promise<PathwayVersion[]> {
    const snap = await getDocs(pathwayVersionsCollection(pathwayId));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as PathwayVersion));
  },

  /** Create a new pathway (parent doc) and its initial version */
  async createPathway(
    pathwayData: Omit<PathwayDoc, 'id'>,
    initialVersion: Omit<PathwayVersion, 'id' | 'pathwayId'>
  ): Promise<{ pathway: PathwayDoc; version: PathwayVersion }> {
    // Create parent doc
    const docRef = await addDoc(appCollection('pathways'), pathwayData);
    const pathway: PathwayDoc = { id: docRef.id, ...pathwayData };

    // Create first version in subcollection
    const versionData = { ...initialVersion, pathwayId: docRef.id };
    const versionRef = await addDoc(pathwayVersionsCollection(docRef.id), versionData);
    const version: PathwayVersion = { id: versionRef.id, ...versionData };

    return { pathway, version };
  },

  /** Create a new version for an existing pathway */
  async createPathwayVersion(
    pathwayId: string,
    versionData: Omit<PathwayVersion, 'id' | 'pathwayId'>,
    parentUpdates?: Partial<PathwayDoc>
  ): Promise<PathwayVersion> {
    const data = { ...versionData, pathwayId };
    const versionRef = await addDoc(pathwayVersionsCollection(pathwayId), data);
    // Update parent doc metadata (dateLastUpdated, lastUpdatedBy, etc.)
    if (parentUpdates) {
      await updateDoc(appDoc('pathways', pathwayId), parentUpdates);
    }
    return { id: versionRef.id, ...data };
  },

  /** Update a pathway parent document (title, metadata) */
  async updatePathway(pathwayId: string, updates: Partial<PathwayDoc>): Promise<void> {
    await updateDoc(appDoc('pathways', pathwayId), updates);
  },

  /** Update a specific version document */
  async updatePathwayVersion(pathwayId: string, versionId: string, updates: Partial<PathwayVersion>): Promise<void> {
    await updateDoc(pathwayVersionDoc(pathwayId, versionId), updates);
  },

  /** Delete a pathway and ALL its versions (cascade delete) */
  async deletePathway(pathwayId: string): Promise<void> {
    // First delete all versions in the subcollection
    const versionsSnap = await getDocs(pathwayVersionsCollection(pathwayId));
    const batch = writeBatch(db);
    versionsSnap.docs.forEach(vDoc => {
      batch.delete(vDoc.ref);
    });
    // Then delete the parent pathway document
    batch.delete(appDoc('pathways', pathwayId));
    await batch.commit();
  },

  /** Delete a single version from a pathway's subcollection */
  async deletePathwayVersion(pathwayId: string, versionId: string): Promise<void> {
    await deleteDoc(pathwayVersionDoc(pathwayId, versionId));
  },

  // --- PATHWAY PROGRESS / ASSIGNMENTS ---
  async getCandidatesForPathway(pathwayId: string, pathwayTitle: string): Promise<PathwayProgress[]> {
    const q = query(appCollection('pathway_assignments'), where('pathwayId', '==', pathwayId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ ...d.data() } as PathwayProgress));
  },

  async getPathwaysForCandidate(candidateId: string): Promise<PathwayProgress[]> {
    const q = query(appCollection('pathway_assignments'), where('candidateId', '==', candidateId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ ...d.data() } as PathwayProgress));
  },

  async assignPathwayToCandidate(assignment: Omit<PathwayProgress, 'id'>): Promise<void> {
    await addDoc(appCollection('pathway_assignments'), assignment);
  },

  async updatePathwayProgress(candidateId: string, pathwayId: string, updates: Partial<PathwayProgress>): Promise<void> {
    const q = query(
      appCollection('pathway_assignments'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, updates);
    }
  },

  async removePathwayAssignment(candidateId: string, pathwayId: string, includeSubPathways?: boolean): Promise<void> {
    // 1. Delete the pathway_assignment doc
    const q = query(
      appCollection('pathway_assignments'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      await deleteDoc(querySnapshot.docs[0].ref);
    }

    // 2. Delete all block_progress docs for this candidate+pathway
    const bpSnap = await getDocs(query(
      appCollection('block_progress'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId)
    ));
    await Promise.all(bpSnap.docs.map(d => deleteDoc(d.ref)));

    // 3. Delete all block_trainingroom_links docs for this candidate+pathway
    const btlSnap = await getDocs(query(
      appCollection('block_trainingroom_links'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId)
    ));
    await Promise.all(btlSnap.docs.map(d => deleteDoc(d.ref)));

    // 4. If requested, cascade delete sub-pathway assignments
    if (includeSubPathways) {
      const versions = await this.getPathwayVersions(pathwayId);
      const published = versions.filter(v => v.status === 'PUBLISHED').sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
      const target = published || versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
      if (target?.blocks) {
        const subPathwayBlocks = target.blocks.filter(b => b.type === 'pathway' && b.linkedPathwayId);
        for (const block of subPathwayBlocks) {
          await this.removePathwayAssignment(candidateId, block.linkedPathwayId!, true);
        }
      }
    }
  },

  // --- OJT SESSION METHODS ---
  async getOjtSessions(orgId: string): Promise<OjtSession[]> {
    const q = query(appCollection('ojt_sessions'), where('orgId', '==', orgId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as OjtSession));
  },

  async getOjtSessionsForCandidate(candidateId: string): Promise<OjtSession[]> {
    const q = query(appCollection('ojt_sessions'), where('candidateId', '==', candidateId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as OjtSession));
  },

  async getOjtSessionForBlock(candidateId: string, pathwayId: string, blockId: string): Promise<OjtSession | null> {
    const q = query(
      appCollection('ojt_sessions'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      orderBy('date', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as OjtSession;
  },

  async createOjtSession(session: Omit<OjtSession, 'id'>): Promise<OjtSession> {
    const docRef = await addDoc(appCollection('ojt_sessions'), session);
    return { id: docRef.id, ...session } as OjtSession;
  },

  async updateOjtSession(session: OjtSession): Promise<void> {
    const { id, ...data } = session;
    await updateDoc(appDoc('ojt_sessions', id), data);
  },

  async deleteOjtSession(sessionId: string): Promise<void> {
    await deleteDoc(appDoc('ojt_sessions', sessionId));
  },

  /** Subscribe to real-time changes on a single OJT session document */
  onOjtSessionChange(sessionId: string, callback: (session: OjtSession) => void): () => void {
    const docRef = appDoc('ojt_sessions', sessionId);
    return onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        callback({ id: snap.id, ...snap.data() } as OjtSession);
      }
    });
  },

  /**
   * Switch the candidate's active OJT mode mid-session.
   * Closes the most recent open segment and appends a new open segment for the next mode.
   * Both the session-wide currentModeId and the modeTimeLog are updated atomically client-side
   * before being written to Firestore.
   */
  async switchOjtSessionMode(session: OjtSession, nextModeId: string, switchTime: number = Date.now()): Promise<OjtSession> {
    const log = [...(session.modeTimeLog || [])];
    const lastIdx = log.length - 1;
    if (lastIdx >= 0 && log[lastIdx].endTime === undefined) {
      const open = log[lastIdx];
      const durationMinutes = Math.max(0, Math.round((switchTime - open.startTime) / 60000));
      log[lastIdx] = { ...open, endTime: switchTime, durationMinutes };
    }
    log.push({ modeId: nextModeId, startTime: switchTime });
    const updated: OjtSession = { ...session, currentModeId: nextModeId, modeTimeLog: log };
    await this.updateOjtSession(updated);
    return updated;
  },

  /**
   * Close the currently open mode segment (e.g. on session completion).
   * No-op if there is no open segment.
   */
  closeOpenModeSegment(session: OjtSession, closeTime: number = Date.now()): OjtSession {
    const log = [...(session.modeTimeLog || [])];
    const lastIdx = log.length - 1;
    if (lastIdx < 0 || log[lastIdx].endTime !== undefined) return session;
    const open = log[lastIdx];
    const durationMinutes = Math.max(0, Math.round((closeTime - open.startTime) / 60000));
    log[lastIdx] = { ...open, endTime: closeTime, durationMinutes };
    return { ...session, modeTimeLog: log };
  },

  // --- TEAM DATA LOADING METHODS ---
  async loadTeamDocument(teamId: string): Promise<any> {
    console.log('📄 Loading team document:', teamId);
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) {
      console.warn('❌ Team document not found:', teamId);
      return null;
    }
    const teamData = teamSnap.data();
    console.log('✅ Team document loaded:', { adminslist: teamData.adminslist?.length, trainees: Object.keys(teamData.trainees || {}).length });
    return teamData;
  },

  async loadAdmins(adminIds: string[]): Promise<any[]> {
    if (!adminIds || adminIds.length === 0) return [];
    console.log('👥 Loading admins:', adminIds.length);
    
    const admins = [];
    for (const adminId of adminIds) {
      const userRef = doc(db, 'userdetails', adminId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        admins.push({
          id: adminId,
          name: data.name || 'Unknown',
          email: data.email || '',
          phoneNumber: data.phoneNumber
        });
      }
    }
    console.log('✅ Admins loaded:', admins.length);
    return admins;
  },

  async loadTrainees(traineesMap: any, includeSubTeams: boolean): Promise<{ candidates: any[], leadhands: any[] }> {
    if (!traineesMap) return { candidates: [], leadhands: [] };
    
    console.log('🎓 Loading trainees, includeSubTeams:', includeSubTeams);
    const traineesToLoad = [];
    
    for (const traineeId in traineesMap) {
      const traineeData = traineesMap[traineeId];
      const hasDirectRole = !!traineeData.role;
      const hasDerivedRole = !!traineeData.derived;
      
      // Always load if direct role, only load derived if includeSubTeams
      if (hasDirectRole || (hasDerivedRole && includeSubTeams)) {
        traineesToLoad.push(traineeId);
      }
    }
    
    console.log('📋 Trainees to load:', traineesToLoad.length);
    
    const candidates = [];
    const leadhands = [];
    
    for (const traineeId of traineesToLoad) {
      const userRef = doc(db, 'userdetails', traineeId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        const traineeTeamData = traineesMap[traineeId];
        
        const trainee = {
          id: traineeId,
          name: data.name || 'Unknown',
          email: data.email || '',
          phoneNumber: data.phoneNumber,
          role: traineeTeamData.role,
          derived: traineeTeamData.derived
        };
        
        // Categorize by team role
        if (traineeTeamData.role === 'LEAD_HAND' || traineeTeamData.derived === 'LEAD_HAND') {
          leadhands.push(trainee);
        } else {
          candidates.push(trainee);
        }
      }
    }
    
    console.log('✅ Trainees loaded - Candidates:', candidates.length, 'Lead Hands:', leadhands.length);
    return { candidates, leadhands };
  },

  async loadAvailableUsers(teamsadminnest: any, activeOrgId: string | undefined, collectionName: string): Promise<any[]> {
    if (!teamsadminnest || !activeOrgId) return [];
    
    // Flatten all team IDs from the hierarchy
    const allTeamIds: string[] = [];
    const flattenTeamIds = (nest: any) => {
      for (const teamId in nest) {
        allTeamIds.push(teamId);
        const children = nest[teamId];
        if (children && typeof children === 'object') {
          flattenTeamIds(children);
        }
      }
    };
    flattenTeamIds(teamsadminnest);
    
    console.log('📇 Loading available users from', allTeamIds.length, 'teams');
    
    // Get user IDs already on the active team
    const activeTeamRef = doc(db, APP_ROOT, 'data', 'teams', activeOrgId);
    const activeTeamSnap = await getDoc(activeTeamRef);
    const currentTraineeIds = activeTeamSnap.exists() 
      ? Object.keys(activeTeamSnap.data()?.[collectionName] || {}) 
      : [];
    
    // Collect unique user IDs from all other teams
    const userIdSet = new Set<string>();
    for (const teamId of allTeamIds) {
      if (teamId === activeOrgId) continue;
      const teamRef = doc(db, APP_ROOT, 'data', 'teams', teamId);
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const teamData = teamSnap.data();
        const members = teamData?.[collectionName] || {};
        for (const userId of Object.keys(members)) {
          if (!currentTraineeIds.includes(userId)) {
            userIdSet.add(userId);
          }
        }
      }
    }
    
    // Load user details
    const availableUsers = [];
    for (const userId of userIdSet) {
      const userRef = doc(db, 'userdetails', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        availableUsers.push({
          id: userId,
          name: data.name || 'Unknown',
          email: data.email || '',
          phoneNumber: data.phoneNumber
        });
      }
    }
    
    console.log('✅ Available users loaded:', availableUsers.length);
    return availableUsers;
  },

  async createActivity(activityData: any): Promise<void> {
    const activitiesRef = appCollection('activities');
    await addDoc(activitiesRef, activityData);
  },

  async getActivities(orgId: string, pageSize: number): Promise<any[]> {
    const activitiesRef = appCollection('activities');
    const q = query(
      activitiesRef,
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  async moveRemoveTrainees(
    uids: string[],
    action: 'Move' | 'Remove',
    teamIdTo: string | null,
    teamIdFrom: string
  ): Promise<{ status: number; message?: string; error?: string; [uid: string]: any }> {
    console.log('🔄 Move/Remove trainees:', { uids, action, teamIdTo, teamIdFrom });
    
    // Get auth token
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }
    const token = await currentUser.getIdToken();
    
    try {
      const response = await fetch(
        'https://europe-west2-tr-staging-8964c.cloudfunctions.net/api/teams/moveremovetrainee',
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            uids: uids,
            action: action,
            teamidto: action === 'Move' ? teamIdTo : null,
            teamidfrom: teamIdFrom
          })
        }
      );
      
      const data = await response.json();
      console.log('📬 Move/Remove trainees response:', data);
      
      return data;
    } catch (error: any) {
      console.error('❌ Move/Remove trainees error:', error);
      return {
        status: 0,
        error: error.message || 'Network error while processing trainees'
      };
    }
  },

  async loadTraineeTeamMemberships(
    userId: string,
    teamsAdminNest: any
  ): Promise<Array<{ teamId: string; teamName: string; role: string }>> {
    console.log('🔍 Loading trainee team memberships for:', userId);
    
    if (!teamsAdminNest || Object.keys(teamsAdminNest).length === 0) {
      return [];
    }
    
    const memberships: Array<{ teamId: string; teamName: string; role: string }> = [];
    
    // Check each team the current admin has access to
    const checkTeam = async (teamId: string, teamName: string) => {
      try {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          
          // Check if this user is a direct trainee in this team
          if (teamData.trainees && teamData.trainees[userId]) {
            const traineeData = teamData.trainees[userId];
            // Only include if they have a direct role (not just derived)
            if (traineeData.role) {
              memberships.push({
                teamId: teamId,
                teamName: teamName || teamData.name || teamId,
                role: traineeData.role
              });
            }
          }
        }
      } catch (error) {
        console.warn('Failed to check team for trainee:', teamId, error);
      }
    };
    
    // Recursively traverse all teams in the admin's hierarchy
    const traverseTeams = async (nest: any) => {
      if (!nest || typeof nest !== 'object') return;
      
      const promises: Promise<void>[] = [];
      
      for (const teamId of Object.keys(nest)) {
        const teamData = nest[teamId];
        if (teamData && typeof teamData === 'object' && teamData.name) {
          promises.push(checkTeam(teamId, teamData.name));
          
          // Recursively check children
          if (teamData.children && typeof teamData.children === 'object') {
            promises.push(traverseTeams(teamData.children));
          }
        }
      }
      
      await Promise.all(promises);
    };
    
    await traverseTeams(teamsAdminNest);
    
    console.log('✅ Trainee team memberships loaded:', memberships.length);
    return memberships;
  },

  // --- TrainingRoom Integration Methods ---

  // Get available course templates for the current admin user
  // Matches TR-admin approach: query by adminslist (array of admin UIDs)
  async getCourseTemplates(): Promise<CourseTemplate[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    const q = query(collection(db, 'coursetemplates'), where('adminslist', 'array-contains', uid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => {
      const data = d.data();
      // Map TR-admin courseData.title.original to a flat 'name' field for display
      const name = data.courseData?.title?.original || data.name || data.title || 'Untitled Template';
      return { id: d.id, name, ...data } as CourseTemplate;
    });
  },

  // Get TrainingRoom courses for a candidate
  async getTrainingRoomCourses(candidateId: string, templateId?: string): Promise<TrainingRoomCourse[]> {
    let q = query(collection(db, 'course'), where('UID', '==', candidateId));
    if (templateId) {
      q = query(q, where('templateid', '==', templateId));
    }
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as TrainingRoomCourse));
  },

  // Check if a course is complete and valid
  checkCourseValidity(course: TrainingRoomCourse): { isComplete: boolean; isValid: boolean; message: string } {
    const isComplete = !!course.signeddate;
    let isValid = isComplete;
    let message = '';
    
    if (isComplete) {
      message = 'Course completed';
      // Check renewal date if it exists
      if (course.renewalDate) {
        const renewalDate = course.renewalDate.toDate ? course.renewalDate.toDate() : new Date(course.renewalDate);
        const now = new Date();
        if (renewalDate < now) {
          isValid = false;
          message = 'Course expired';
        }
      }
    } else {
      message = 'Course not complete';
    }
    
    return { isComplete, isValid, message };
  },

  // Create a link between block and course for a candidate
  async createBlockTrainingRoomLink(link: Omit<BlockTrainingRoomLink, 'id'>): Promise<BlockTrainingRoomLink> {
    const docRef = await addDoc(appCollection('block_trainingroom_links'), link);
    return { id: docRef.id, ...link };
  },

  // Get TrainingRoom link for a specific candidate+block
  async getBlockTrainingRoomLink(candidateId: string, blockId: string, pathwayId: string): Promise<BlockTrainingRoomLink | null> {
    const q = query(
      appCollection('block_trainingroom_links'),
      where('candidateId', '==', candidateId),
      where('blockId', '==', blockId),
      where('pathwayId', '==', pathwayId)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const docSnap = querySnapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() } as BlockTrainingRoomLink;
  },

  // Update link status
  async updateBlockTrainingRoomLink(linkId: string, updates: Partial<BlockTrainingRoomLink>): Promise<void> {
    await updateDoc(appDoc('block_trainingroom_links', linkId), updates);
  },

  // Create a course request in TrainingRoom
  async createTrainingRoomCourseRequest(templateId: string, candidateId: string, orgId: string): Promise<{ courseId: string; accessCode: number }> {
    // Constants
    const COURSE_DUE_DATE_DAYS = 30;
    
    // Generate unique IDs
    const courseId = `course_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const accessCode = Math.floor(100000 + Math.random() * 900000);
    
    // Create course request in TrainingRoom structure
    const courseReqRef = doc(db, 'coursetemplates', templateId, 'coursereq', courseId);
    await setDoc(courseReqRef, {
      UID: candidateId,
      accesscode: accessCode,
      courseid: courseId,
      teamid: orgId,
      templateid: templateId,
      reqby: 'SoloReady',
      duedate: new Date(Date.now() + COURSE_DUE_DATE_DAYS * 24 * 60 * 60 * 1000),
      createdAt: new Date()
    });
    
    // Create access code document
    const accessCodeRef = doc(db, 'accesscodes', accessCode.toString());
    await setDoc(accessCodeRef, {
      type: 'course',
      courseid: courseId
    });
    
    return { courseId, accessCode };
  },

  // --- BLOCK PROGRESS METHODS ---

  async getBlockProgressForPathway(candidateId: string, pathwayId: string): Promise<any[]> {
    const snap = await getDocs(query(
      appCollection('block_progress'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId)
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  async getBlockProgress(candidateId: string, pathwayId: string, blockId: string): Promise<any | null> {
    const snap = await getDocs(query(
      appCollection('block_progress'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      limit(1)
    ));
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  },

  async setBlockProgress(progress: { candidateId: string; pathwayId: string; blockId: string; status: string; completedAt?: number }): Promise<void> {
    const snap = await getDocs(query(
      appCollection('block_progress'),
      where('candidateId', '==', progress.candidateId),
      where('pathwayId', '==', progress.pathwayId),
      where('blockId', '==', progress.blockId),
      limit(1)
    ));
    const data = { ...progress, updatedAt: Date.now() };
    if (snap.empty) {
      await addDoc(appCollection('block_progress'), data);
    } else {
      await updateDoc(snap.docs[0].ref, data);
    }
  },

  // --- OJT PIN NOTIFICATION ---

  async sendOjtPin(leadHandUserId: string, leadHandName: string, pin: string, sessionId: string, candidateName: string): Promise<void> {
    await addDoc(collection(db, 'notifications'), {
      uid: leadHandUserId,
      area: 'SoloReady',
      event: 'OJT_PIN',
      text: `SoloReady: Your OJT session PIN for ${candidateName}'s session is: ${pin}. Do not share this code.`,
      sendat: new Date(),
      reqby: 'System',
      source: sessionId,
      phoneonly: true,
      smsfrom: 'SoloReady',
      createdAt: new Date(),
      nanoTimestamp: Date.now(),
    });
  },

  // --- LEAD HAND LOOKUP ---

  /** Fetch the latest published (or most recent) version of a pathway by its root (parent doc) ID */
  async getPathwayByRootId(rootId: string): Promise<Pathway | null> {
    const parentRef = appDoc('pathways', rootId);
    const parentSnap = await getDoc(parentRef);
    if (!parentSnap.exists()) return null;
    const parent = { id: parentSnap.id, ...parentSnap.data() } as PathwayDoc;

    const versionsSnap = await getDocs(pathwayVersionsCollection(rootId));
    if (versionsSnap.empty) {
      return {
        id: parent.id,
        rootId: parent.id,
        title: parent.title || 'Untitled',
        role: parent.role || '',
        lastEdited: parent.lastEdited || parent.dateCreated || '',
        status: parent.status || 'DRAFT',
        version: 1.0,
        blocks: [],
        description: parent.description || '',
        soloreadyOutline: parent.soloreadyOutline || '',
      };
    }

    const versions = versionsSnap.docs.map(d => ({ id: d.id, ...d.data() } as PathwayVersion));
    // Prefer PUBLISHED version; fall back to the last version in the list
    const published = versions.find(v => v.status === 'PUBLISHED');
    const chosen = published || versions[versions.length - 1];

    return {
      id: chosen.id,
      rootId: parent.id,
      title: parent.title || 'Untitled',
      role: parent.role || '',
      lastEdited: parent.lastEdited || parent.dateLastUpdated || parent.dateCreated || '',
      status: chosen.status || parent.status || 'DRAFT',
      version: chosen.version ?? 1.0,
      blocks: chosen.blocks || [],
      description: parent.description || '',
      soloreadyOutline: parent.soloreadyOutline || '',
    };
  },

  async getLeadHandDetails(userIds: string[]): Promise<any[]> {
    if (!userIds || userIds.length === 0) return [];
    const results: any[] = [];
    const chunks: string[][] = [];
    for (let i = 0; i < userIds.length; i += 10) {
      chunks.push(userIds.slice(i, i + 10));
    }
    for (const chunk of chunks) {
      const snap = await getDocs(query(collection(db, 'userdetails'), where('__name__', 'in', chunk)));
      snap.docs.forEach(d => results.push({ id: d.id, ...d.data() }));
    }
    return results;
  },

  // --- PRACTICAL EVALUATION ---

  async getPracticalEvaluation(candidateId: string, pathwayId: string, blockId: string): Promise<PracticalEvaluation | null> {
    const snap = await getDocs(query(
      appCollection('practical_evaluations'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      limit(1)
    ));
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as PracticalEvaluation;
  },

  // --- ASSESSMENT ATTEMPTS ---

  async getAssessmentAttempts(candidateId: string, pathwayId: string, blockId: string): Promise<AssessmentAttempt[]> {
    const snap = await getDocs(query(
      appCollection('assessment_attempts'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      orderBy('attemptNumber', 'asc')
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AssessmentAttempt));
  },

  async saveAssessmentAttempt(attempt: Omit<AssessmentAttempt, 'id'>): Promise<AssessmentAttempt> {
    const docRef = await addDoc(appCollection('assessment_attempts'), attempt);
    return { id: docRef.id, ...attempt };
  },

  // --- BLOCK UPLOADS ---

  async getBlockUpload(candidateId: string, pathwayId: string, blockId: string): Promise<BlockUpload | null> {
    const snap = await getDocs(query(
      appCollection('block_uploads'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      limit(1)
    ));
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as BlockUpload;
  },

  async saveBlockUpload(upload: Omit<BlockUpload, 'id'>): Promise<BlockUpload> {
    const docRef = await addDoc(appCollection('block_uploads'), upload);
    return { id: docRef.id, ...upload };
  },

  async updateBlockUpload(id: string, updates: Partial<BlockUpload>): Promise<void> {
    await updateDoc(appDoc('block_uploads', id), updates);
  },

  uploadBlockFile(file: File, path: string): Promise<string> {
    const storage = getStorage();
    const storageRef = ref(storage, `${STORAGE_ROOT}/${path}`);
    return uploadBytes(storageRef, file).then(snap => getDownloadURL(snap.ref));
  },

  // --- API VERIFICATION ---

  async getApiVerification(candidateId: string, pathwayId: string, blockId: string): Promise<ApiVerification | null> {
    const snap = await getDocs(query(
      appCollection('api_verifications'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      limit(1)
    ));
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as ApiVerification;
  },

  // --- PATHWAY AUTHORISATION ---

  async getPathwayAuthorisation(candidateId: string, pathwayId: string, blockId: string): Promise<PathwayAuthorisation | null> {
    const snap = await getDocs(query(
      appCollection('pathway_authorisations'),
      where('candidateId', '==', candidateId),
      where('pathwayId', '==', pathwayId),
      where('blockId', '==', blockId),
      limit(1)
    ));
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as PathwayAuthorisation;
  },

  // --- AGENT-LED OJT MESSAGING HELPERS ---

  subscribeOjtMessages(sessionId: string, callback: (messages: OjtMessage[]) => void): () => void {
    const q = query(
      appCollection('ojt_messages'),
      where('sessionId', '==', sessionId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as OjtMessage)));
    });
  },

  async sendOjtChatMessage(sessionId: string, text: string): Promise<void> {
    await addDoc(appCollection('ojt_messages'), {
      sessionId,
      role: 'candidate',
      kind: 'text',
      body: text,
      createdAt: Date.now()
    });
  },
};